/**
 * Repository utilities for blog metadata and MDX file lookup.
 *
 * What: Exposes sorted blog listing, slug-based retrieval, related-post selection, and mapped MDX file paths.
 * Why: Keeps blog data-access behavior centralized so pages can focus on rendering and SEO concerns.
 * When: Update when catalog fields change, sorting rules evolve, or related-post heuristics are adjusted.
 */

import type { BlogPostCatalogEntry } from '@shared/blog/blogPostCatalog';
import {
  BLOG_POST_CATALOG,
  BLOG_POST_CONTENT_DIRECTORY,
  getBlogPostCatalogEntryBySlug,
  normalizeBlogSlug,
} from '@shared/blog/blogPostCatalog';

const DEFAULT_RELATED_POSTS_LIMIT = 3;

export interface BlogPostRecord extends BlogPostCatalogEntry {
  tags: string[];
  contentFilePath: string;
}

const BLOG_POSTS_SORTED_BY_FRESHNESS: readonly BlogPostCatalogEntry[] =
  Object.freeze([...BLOG_POST_CATALOG].sort(compareByFreshnessDesc));

/**
 * Return all blog posts sorted by most recent activity (updated or published) first.
 */
export function listBlogPosts(): BlogPostRecord[] {
  return BLOG_POSTS_SORTED_BY_FRESHNESS.map(toBlogPostRecord);
}

/**
 * Resolve a single blog post by slug.
 */
export function getBlogPostBySlug(rawSlug: string): BlogPostRecord | null {
  const entry = getBlogPostCatalogEntryBySlug(rawSlug);
  return entry ? toBlogPostRecord(entry) : null;
}

/**
 * Check whether a slug maps to a known blog post.
 */
export function hasBlogPost(rawSlug: string): boolean {
  return Boolean(getBlogPostCatalogEntryBySlug(rawSlug));
}

/**
 * Build a repository path for the MDX source file of a slug.
 */
export function getBlogPostContentFilePath(rawSlug: string): string | null {
  const normalizedSlug = normalizeBlogSlug(rawSlug);
  if (!normalizedSlug) {
    return null;
  }

  const entry = getBlogPostCatalogEntryBySlug(normalizedSlug);
  if (!entry) {
    return null;
  }

  return `${BLOG_POST_CONTENT_DIRECTORY}/${entry.contentFileName}`;
}

interface ListRelatedOptions {
  limit?: number;
}

/**
 * Return related-post candidates for a blog slug.
 */
export function listRelatedBlogPosts(
  rawSlug: string,
  { limit = DEFAULT_RELATED_POSTS_LIMIT }: ListRelatedOptions = {}
): BlogPostRecord[] {
  const sourcePost = getBlogPostCatalogEntryBySlug(rawSlug);
  if (!sourcePost) {
    return [];
  }

  const normalizedLimit = normalizeRelatedPostLimit(limit);
  const sourceTagSet = new Set(sourcePost.tags);

  return BLOG_POSTS_SORTED_BY_FRESHNESS.filter(
    candidate => candidate.slug !== sourcePost.slug
  )
    .map(candidate => ({
      candidate,
      score: computeRelatedScore(candidate, sourcePost, sourceTagSet),
    }))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return compareByFreshnessDesc(left.candidate, right.candidate);
    })
    .slice(0, normalizedLimit)
    .map(({ candidate }) => toBlogPostRecord(candidate));
}

function toBlogPostRecord(entry: BlogPostCatalogEntry): BlogPostRecord {
  return {
    ...entry,
    tags: [...entry.tags],
    contentFilePath: `${BLOG_POST_CONTENT_DIRECTORY}/${entry.contentFileName}`,
  };
}

function normalizeRelatedPostLimit(limit: number): number {
  const numericLimit = Number(limit);

  if (!Number.isFinite(numericLimit)) {
    return DEFAULT_RELATED_POSTS_LIMIT;
  }

  return Math.max(0, Math.floor(numericLimit));
}

function getFreshnessTimestamp(entry: BlogPostCatalogEntry): number {
  const published = Date.parse(entry.publishDate);
  const updated = entry.updatedDate ? Date.parse(entry.updatedDate) : 0;
  return Math.max(published, updated);
}

function compareByFreshnessDesc(
  left: BlogPostCatalogEntry,
  right: BlogPostCatalogEntry
): number {
  const leftTimestamp = getFreshnessTimestamp(left);
  const rightTimestamp = getFreshnessTimestamp(right);

  if (rightTimestamp !== leftTimestamp) {
    return rightTimestamp - leftTimestamp;
  }

  return left.slug.localeCompare(right.slug);
}

function computeRelatedScore(
  candidate: BlogPostCatalogEntry,
  sourcePost: BlogPostCatalogEntry,
  sourceTagSet: Set<string>
): number {
  let score = 0;

  if (candidate.category === sourcePost.category) {
    score += 4;
  }

  if (candidate.audience === sourcePost.audience) {
    score += 2;
  }

  const sharedTagCount = candidate.tags.reduce(
    (count, tag) => count + (sourceTagSet.has(tag) ? 1 : 0),
    0
  );
  score += sharedTagCount * 3;

  return score;
}
